import axiosInstance from '../../utils/axios';
import { AppointmentBookingRequest, AppointmentBookingResponse } from '../types/appointmentBooking';
import { TimeSlot } from '../hooks/useAppointmentBooking';

export const appointmentBookingService = {
  getMe: async () => {
    const response = await axiosInstance.get('/api/v1/me');
    return response.data;
  },

  getEvents: async () => {
    const response = await axiosInstance.get('/api/v1/events');
    return response.data;
  },

  bookAppointment: async (bookingData: AppointmentBookingRequest): Promise<AppointmentBookingResponse> => {
    try {
      // Copy workTypeGroupId to appointmentId as per requirement
      const requestData = {
        ...bookingData,
        appointmentId: bookingData.workTypeGroupId
      };
      
      const response = await axiosInstance.post<AppointmentBookingResponse>(
        '/api/v1/appointments',
        requestData
      );
      return response.data;
    } catch (error: any) {
      throw {
        message: error.response?.data?.message || 'Failed to book appointment',
        status: error.response?.status
      };
    }
  },
  
  getAvailableSlots: async (appointmentId: string, showroom: string): Promise<{ data: TimeSlot[] } | TimeSlot[]> => {
    try {
      // Backend expects enum values: Montreal, Royalmount (exact casing!), Toronto
      // Frontend receives full names: "Montreal Sales Floor 1", "ROYALMOUNT Sales Floor 3", etc.
      // Mapping logic: Extract the enum value from the full showroom name using string matching
      
      const showroomLower = showroom.toLowerCase();
      let showroomParam: string;
      
      // Map full showroom names to backend enum values
      if (showroomLower.includes('montreal') && !showroomLower.includes('royal')) {
        // Handles: "Montreal Live Kitchen", "Montreal Sales Floor 1/2/3", "Montreal Boardroom"
        showroomParam = 'Montreal';
      } else if (showroomLower.includes('royal')) {
        // Handles: "ROYALMOUNT Sales Floor 1/2/3", "ROYALMOUNT Boardroom"
        // CRITICAL: Backend requires exact casing "Royalmount" (capital R, lowercase rest)
        showroomParam = 'Royalmount';
      } else if (showroomLower.includes('toronto')) {
        // Handles: "Toronto Live Kitchen", "Toronto Sales Floor 1/2/3", "Toronto Boardroom"
        showroomParam = 'Toronto';
      } else {
        // Fallback: use the original value if no match found
        console.warn(`Unknown showroom format: "${showroom}". Using original value.`);
        showroomParam = showroom;
      }
      
      const response = await axiosInstance.get(
        `/api/v1/appointments/available-slots/${appointmentId}?showroom=${encodeURIComponent(showroomParam)}`
      );
      
      // Handle both formats: direct array or wrapped in data property
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        return response.data;
      } else {
        // If neither format matches, return an empty data array
        return { data: [] };
      }
    } catch (error: any) {
      throw {
        message: error.response?.data?.message || 'Failed to fetch available slots',
        status: error.response?.status
      };
    }
  }
};
